import { useCallback, useRef } from 'react'

// Wires a horizontally-scrolling strip and returns a ref callback for it. Two jobs:
//  1. Translate a plain vertical mouse wheel into horizontal scroll (no-ops when the strip isn't
//     overflowing — e.g. the desktop wrapped layout — or when the gesture is already horizontal).
//  2. Toggle `data-at-end` so the right-edge fade can hide once there's nothing more to scroll to
//     (and when the chips don't overflow at all). Recomputed on scroll, resize and content changes.
export const useWheelScroll = <T extends HTMLElement>() => {
	const cleanupRef = useRef<(() => void) | null>(null)

	return useCallback((node: T | null) => {
		cleanupRef.current?.()
		cleanupRef.current = null
		if (!node) return

		const onWheel = (e: WheelEvent) => {
			if (node.scrollWidth <= node.clientWidth) return
			if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return
			node.scrollLeft += e.deltaY
			e.preventDefault()
		}

		const updateEdge = () => {
			// +1 guards against sub-pixel rounding leaving us 0.5px short of the true end.
			const atEnd = Math.ceil(node.scrollLeft + node.clientWidth) + 1 >= node.scrollWidth
			node.dataset.atEnd = String(atEnd)
		}

		updateEdge()
		node.addEventListener('wheel', onWheel, { passive: false })
		node.addEventListener('scroll', updateEdge, { passive: true })

		const resizeObserver = new ResizeObserver(updateEdge)
		resizeObserver.observe(node)
		const mutationObserver = new MutationObserver(updateEdge)
		mutationObserver.observe(node, { childList: true, subtree: true })

		cleanupRef.current = () => {
			node.removeEventListener('wheel', onWheel)
			node.removeEventListener('scroll', updateEdge)
			resizeObserver.disconnect()
			mutationObserver.disconnect()
		}
	}, [])
}
